import React from "react";
import ModalScreen from "../common/ModalScreen";
import { Check, X } from "lucide-react";
import OfferProposal from "../../model/OfferProposal";

interface ConfirmOrderConfirmationProps {
  openModal: boolean;
  onClose: () => void;
  onConfirm: () => void;
  offer: OfferProposal;
}

const ConfirmOrderConfirmation: React.FC<ConfirmOrderConfirmationProps> = ({
  openModal,
  onClose,
  onConfirm,
  offer,
}) => {
  if (!openModal) return null;

  const calculateTotals = () => {
    let totalCost = 0;
    let totalSellExVat = 0;
    let totalSellIncVat = 0;

    const getWithoutVat = (price: any) => {
      if (price.without_vat && price.without_vat > 0) return price.without_vat;
      const vatRate =
        price.vat_rate !== undefined && price.vat_rate !== null
          ? price.vat_rate
          : 23;
      return price.value / (1 + vatRate / 100);
    };

    // Base Price (assuming compound price is the base)
    const basePrice = offer.compound.price;
    totalCost += basePrice.resell || 0;
    totalSellExVat += getWithoutVat(basePrice);
    totalSellIncVat += basePrice.value || 0;

    return { totalCost, totalSellExVat, totalSellIncVat };
  };

  const { totalCost, totalSellExVat, totalSellIncVat } = calculateTotals();
  const margin = totalSellExVat - totalCost;
  const marginPercent = totalCost > 0 ? (margin / totalCost) * 100 : 0;

  return (
    <ModalScreen
      title="Confirmar Encomenda"
      onClose={onClose}
      state="confirm"
      footer={
        <div className="flex justify-end gap-2 p-4">
          <button className="button is-white" onClick={onClose}>
            Cancelar
          </button>
          <button className="button is-success" onClick={onConfirm}>
            <span className="icon is-small">
              <Check className="inline mr-1" />
            </span>
            <span>Confirmar Encomenda</span>
          </button>
        </div>
      }
    >
      <div className="p-6">
        <div className="notification is-warning is-light mb-6 flex items-start">
          <span className="icon has-text-warning mr-3 mt-1">
            <i className="fas fa-exclamation-triangle"></i>
          </span>
          <div>
            <p className="font-bold text-gray-800">
              Tem a certeza que deseja criar uma encomenda?
            </p>
            <p className="text-sm mt-1 text-gray-600">
              Esta ação irá gerar uma encomenda no sistema. Verifique os valores
              abaixo antes de confirmar.
            </p>
          </div>
        </div>

        <div className="box shadow-sm border border-gray-100">
          <h3 className="title is-6 mb-4 border-b pb-2 text-gray-700">
            Resumo Financeiro
          </h3>
          <div className="columns is-multiline is-mobile">
            <div className="column is-6-mobile is-6-tablet">
              <div className="field mb-2">
                <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                  O Seu Custo (Revenda)
                </label>
                <div className="control">
                  <span className="title is-5 text-gray-700 font-bold">
                    {totalCost.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
            <div className="column is-6-mobile is-6-tablet">
              <div className="field mb-2">
                <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                  Margem Estimada
                </label>
                <div className="control">
                  <span
                    className={`title is-5 font-bold ${margin >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {margin.toFixed(2)} €{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({marginPercent.toFixed(1)}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="column is-6-mobile is-6-tablet">
              <div className="field mb-2">
                <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                  Preço Venda (s/ IVA)
                </label>
                <div className="control">
                  <span className="title is-5 text-blue-600 font-bold">
                    {totalSellExVat.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
            <div className="column is-6-mobile is-6-tablet">
              <div className="field mb-2">
                <label className="label text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                  Preço Venda (c/ IVA)
                </label>
                <div className="control">
                  <span className="title is-5 text-blue-800 font-bold">
                    {totalSellIncVat.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalScreen>
  );
};

export default ConfirmOrderConfirmation;
